import { memo, useCallback, useMemo } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { type TableViewProps } from '~/components/TableView';
import { CustomFieldTableHeader } from '~/components/TableView/CustomFieldTableHeader/CustomFieldTableHeader';
import { SelectableTableView, type SelectableTableViewProps } from '~/components/TableView/SelectableTableView';
import { TableHeaders } from '~/components/TableView/TableHeaders';
import { TablePageContainer } from '~/components/TableView/TablePageContainer';
import { usePublicTableRows } from '~/components/TableView/usePublicTableRows';
import { useTableSelectableItems } from '~/components/TableView/useTableSelectableItems';
import { BOARD_PAGE_TABLE_VIEW } from '~/constants/testIDs';
import { useTableHeaderPosition } from '~/hooks/useTableHeaderPosition';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { usePublicTableViewItems } from '~/swr-hooks/gallery/tableView/usePublicTableViewItems';
import { canChangeBoardCustomFields } from '~/utils/permissions/boardPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';
import { TableViewProvider } from '../TableView/TableViewContext';
import { PublicBoardNullState } from './PublicBoardNullState';

export type PublicBoardTableViewProps = Pick<SelectableTableViewProps, 'scrollElementRef'>;

export const PublicBoardTableView = memo(({ scrollElementRef }: PublicBoardTableViewProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { tableHeaderTopPos } = useTableHeaderPosition();
  const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
  const { data, hasMore, isEmpty, isLoading, isLoadingMore, loadNextPage } = usePublicTableViewItems();

  const { permissions } = usePublicPermissions();
  const canEditCustomFields = canChangeBoardCustomFields(permissions);
  const canDownload = canGenerateZip(permissions);

  /**
   * If the parent shortUrl has the `asset.canViewOriginal` or `customField.canManageApplications` permission,
   * the user can select all items (assets or boards) in the table.
   */
  const canSelectItems = canDownload || canEditCustomFields;

  const rows = usePublicTableRows({
    data,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore: loadNextPage,
    isAssetSelectable: canSelectItems,
    isBoardSelectable: canSelectItems,
  });

  const selectableItems = useMemo(() => (canSelectItems ? data : []), [canSelectItems, data]);

  useTableSelectableItems(selectableItems);

  const hasData = !!data?.length;

  const renderTableHeaders: Required<TableViewProps>['renderHeaders'] = useCallback(
    (ref) =>
      hasData &&
      isAboveMediumScreen && (
        <TableHeaders
          ref={ref}
          CustomFieldTableHeaderComponent={CustomFieldTableHeader}
          horizontalPadding={responsiveHorizontalPadding}
          topPos={tableHeaderTopPos}
        />
      ),
    [hasData, isAboveMediumScreen, responsiveHorizontalPadding, tableHeaderTopPos],
  );

  return (
    <TableViewProvider>
      <TablePageContainer data-testid={BOARD_PAGE_TABLE_VIEW}>
        {isEmpty && typeof document !== 'undefined' ? (
          <PublicBoardNullState />
        ) : (
          <SelectableTableView
            canScrollHorizontally={isAboveMediumScreen && hasData}
            horizontalPadding={responsiveHorizontalPadding}
            renderHeaders={renderTableHeaders}
            rows={rows}
            scrollElementRef={scrollElementRef}
          />
        )}
      </TablePageContainer>
    </TableViewProvider>
  );
});

PublicBoardTableView.displayName = 'PublicBoardTableView';
